/* Poise theme - vanilla JS, web components, no dependencies. */
(() => {
  const theme = window.theme || {};
  const qs = (s, el = document) => el.querySelector(s);
  const qsa = (s, el = document) => Array.from(el.querySelectorAll(s));
  const debounce = (fn, ms = 300) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    const fmt = String(format || theme.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
    const value = typeof cents === 'string' ? Number(cents.replace('.', '')) : Number(cents);
    const delimit = (precision, thousands = ',', decimal = '.') => {
      const [whole, frac] = (value / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const formats = {
      amount: () => delimit(2),
      amount_no_decimals: () => delimit(0),
      amount_with_comma_separator: () => delimit(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => delimit(0, '.', ','),
      amount_with_apostrophe_separator: () => delimit(2, "'", '.'),
      amount_no_decimals_with_space_separator: () => delimit(0, ' ', ''),
      amount_with_space_separator: () => delimit(2, ' ', ','),
      amount_with_period_and_space_separator: () => delimit(2, ' ', '.')
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (formats[match && match[1]] || formats.amount)());
  }
  theme.formatMoney = formatMoney;

  /* ---------- Dialogs: drawers, modals, lightbox ---------- */
  let lastOpener = null;
  function openDialog(id, opener) {
    const d = document.getElementById(id);
    if (!d || typeof d.showModal !== 'function') return false;
    lastOpener = opener || document.activeElement;
    if (!d.open) d.showModal();
    d.dispatchEvent(new CustomEvent('dialog:open', { detail: { opener: lastOpener } }));
    return true;
  }
  theme.openDialog = openDialog;
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (opener) {
      if (openDialog(opener.dataset.openDialog, opener)) {
        e.preventDefault();
        // The drawer is rendered at page load: refresh it when opened by hand, in case the cart
        // changed in another tab or the page came back from the back-forward cache.
        if (opener.dataset.openDialog === 'CartDrawer') refreshCartDrawer();
      }
      return;
    }
    const closer = e.target.closest('[data-close-dialog]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.open) {
      const r = e.target.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) e.target.close();
    }
  });
  document.addEventListener('close', (e) => {
    if (e.target.tagName === 'DIALOG' && lastOpener && document.contains(lastOpener)) lastOpener.focus({ preventScroll: true });
  }, true);
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-auto-submit]')) e.target.form?.submit();
  });

  /* ---------- Header: height variable + scrolled state ---------- */
  define('sticky-header', class extends HTMLElement {
    connectedCallback() {
      const setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      setHeight();
      new ResizeObserver(setHeight).observe(this);
      if (!this.classList.contains('is-sticky')) return;
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  });

  /* ---------- Announcement bar ---------- */
  define('announcement-bar', class extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.announcement__item', this);
      this.index = 0;
      this.show(0);
      if (this.items.length < 2) return;
      qs('[data-prev]', this)?.addEventListener('click', () => this.go(-1));
      qs('[data-next]', this)?.addEventListener('click', () => this.go(1));
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      if (reducedMotion || window.Shopify?.designMode) return;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      const pause = qs('[data-pause]', this);
      pause?.addEventListener('click', () => {
        this.paused = !this.paused;
        pause.setAttribute('aria-pressed', String(this.paused));
        pause.setAttribute('aria-label', this.paused ? pause.dataset.labelPlay : pause.dataset.labelPause);
        pause.classList.toggle('is-paused', this.paused);
        if (this.paused) this.stop(); else this.start();
      });
    }
    show(i) { this.items.forEach((el, n) => { el.classList.toggle('is-active', n === i); el.setAttribute('aria-hidden', String(n !== i)); }); }
    go(step) { this.index = (this.index + step + this.items.length) % this.items.length; this.show(this.index); }
    start() { this.stop(); if (this.paused) return; this.timer = setInterval(() => this.go(1), this.speed); }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() { this.stop(); }
  });

  /* ---------- Countdown (offer end date, or daily reset at midnight UK) ---------- */
  function londonNow() {
    const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
    const g = (t) => parseInt(p.find((x) => x.type === t).value, 10);
    return new Date(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second'));
  }
  define('countdown-timer', class extends HTMLElement {
    connectedCallback() { this.tick(); this.timer = setInterval(() => this.tick(), 1000); }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let left;
      const end = Date.parse(this.dataset.end || '');
      if (!isNaN(end)) left = end - Date.now();
      else { const now = londonNow(); const midnight = new Date(now); midnight.setHours(24, 0, 0, 0); left = midnight - now; }
      if (left <= 0) {
        clearInterval(this.timer);
        const host = this.closest('[data-countdown-host]');
        if (host && this.hasAttribute('data-hide-expired')) host.hidden = true;
        this.textContent = '00:00:00';
        return;
      }
      const d = Math.floor(left / 864e5), h = Math.floor(left / 36e5) % 24, m = Math.floor(left / 6e4) % 60, s = Math.floor(left / 1e3) % 60;
      const pad = (n) => String(n).padStart(2, '0');
      this.textContent = (d > 0 ? `${d}d ` : '') + `${pad(h)}:${pad(m)}:${pad(s)}`;
    }
  });

  /* ---------- Delivery estimate (UK working days, dispatch cut-off) ---------- */
  define('delivery-estimate', class extends HTMLElement {
    connectedCallback() { this.render(); this.timer = setInterval(() => this.render(), 30000); }
    disconnectedCallback() { clearInterval(this.timer); }
    workday(d) { const day = d.getDay(); return this.dataset.skipWeekends !== 'true' || (day !== 0 && day !== 6); }
    addWorkdays(date, n) { const r = new Date(date); while (n > 0) { r.setDate(r.getDate() + 1); if (this.workday(r)) n -= 1; } return r; }
    render() {
      const out = qs('[data-delivery-text]', this);
      if (!out) return;
      const now = londonNow();
      const cutoff = parseInt(this.dataset.cutoff, 10) || 14;
      const today = this.workday(now) && now.getHours() < cutoff;
      const dispatch = today ? now : this.addWorkdays(now, 1);
      const from = this.addWorkdays(dispatch, parseInt(this.dataset.min, 10) || 1);
      const to = this.addWorkdays(dispatch, parseInt(this.dataset.max, 10) || 3);
      const fmt = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      let lead;
      if (today) {
        const mins = cutoff * 60 - (now.getHours() * 60 + now.getMinutes());
        lead = this.dataset.todayText.replace('{time}', `<strong>${Math.floor(mins / 60)}h ${mins % 60}m</strong>`);
      } else {
        lead = this.dataset.laterText.replace('{day}', `<strong>${fmt(dispatch)}</strong>`);
      }
      const range = this.dataset.rangeText.replace('{from}', `<strong>${fmt(from)}</strong>`).replace('{to}', `<strong>${fmt(to)}</strong>`);
      out.innerHTML = `${lead}<br>${range}`;
    }
  });

  /* ---------- Cart (Ajax + Section Rendering API) ---------- */
  function updateCartSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-count') {
        const n = qs('[data-cart-count]', doc)?.textContent.trim() || '';
        qsa('[data-cart-count]').forEach((el) => { el.textContent = n; });
        return;
      }
      qsa('[data-cart-region]', doc).forEach((fresh) => {
        const cur = qs(`[data-cart-region="${fresh.dataset.cartRegion}"]`);
        if (cur) cur.replaceWith(fresh);
      });
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }
  function cartSectionIds() {
    const ids = ['cart-count'];
    if (qs('#CartDrawer')) ids.push('cart-drawer');
    qsa('cart-items[data-section]').forEach((el) => ids.push(el.dataset.section));
    return [...new Set(ids)];
  }
  async function refreshCartDrawer() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const r = await fetch(`${root}?sections=${cartSectionIds().join(',')}`, { headers: { Accept: 'application/json' } });
      if (r.ok) updateCartSections(await r.json());
    } catch (err) { /* keep the drawer as rendered */ }
  }
  theme.updateCartSections = updateCartSections;

  define('product-form', class extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      this.error = qs('.product-form__error', this);
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      // Only the main product form owns a pack picker or bundle offers; quick-add forms
      // (recommendations, cart upsells) must never pick up the section's choices.
      const section = this.closest('[data-product-section]');
      const pack = section ? qs('pack-picker', section) : null;
      if (pack && !pack.validate()) { e.preventDefault(); return; }
      const lines = section ? qs('bundle-offers', section)?.items() : null;
      if (lines) { this.addLines(e, lines); return; }
      if (!qs('#CartDrawer')) return; // cart page mode: native POST to /cart/add
      e.preventDefault();
      const btn = e.submitter || qs('[type="submit"]', this.form);
      if (!btn || btn.classList.contains('is-loading')) return;
      const wasDisabled = btn.disabled;
      btn.classList.add('is-loading');
      btn.disabled = true;
      if (this.error) this.error.textContent = '';
      const fd = new FormData(this.form);
      if (e.submitter && e.submitter.name === 'id') fd.set('id', e.submitter.value);
      fd.append('sections', cartSectionIds().join(','));
      fd.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
        const data = await res.json();
        if (!res.ok || data.status) {
          const msg = data.description || data.message || theme.strings.error;
          if (this.error) this.error.textContent = msg; else window.alert(msg);
          return;
        }
        updateCartSections(data.sections);
        openDialog('CartDrawer', btn);
      } catch (err) {
        if (this.error) this.error.textContent = theme.strings.error;
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = wasDisabled;
      }
    }
    // Several cart lines at once (bundle offers): JSON add, then drawer or cart page
    async addLines(e, lines) {
      e.preventDefault();
      const btn = e.submitter || qs('[type="submit"]', this.form);
      if (!lines.length || !btn || btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      btn.disabled = true;
      if (this.error) this.error.textContent = '';
      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: lines, sections: cartSectionIds(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          const msg = data.description || data.message || theme.strings.error;
          if (this.error) this.error.textContent = msg; else window.alert(msg);
          return;
        }
        if (!qs('#CartDrawer')) { window.location.href = theme.routes.cart; return; }
        updateCartSections(data.sections);
        openDialog('CartDrawer', btn);
      } catch (err) {
        if (this.error) this.error.textContent = theme.strings.error;
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }
  });

  /* ---------- Product: bundle offers (multi-buy tiers, one colour/size choice per item) ---------- */
  define('bundle-offers', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]') || document;
      try { const data = JSON.parse(qs('script[data-bundle-variants]', this).textContent); this.variants = data.variants || []; this.swatches = data.swatches || {}; }
      catch (e) { this.variants = []; this.swatches = {}; }
      this.colorIndex = Number(this.dataset.colorIndex);
      this.tiers = qsa('[data-bundle-tier]', this);
      this.idInput = qs(`input[name="id"][data-product-id="${this.dataset.productId}"]`, this.section);
      this.addEventListener('change', (e) => {
        const tier = e.target.closest('[data-bundle-tier]');
        if (e.target.matches('.bundle__input')) { this.select(tier); return; }
        if (e.target.matches('[data-bundle-option]')) {
          tier.dataset.dirty = 'true';
          this.refreshItem(e.target.closest('[data-bundle-item]'));
          this.update();
        }
      });
      // the main variant picker drives the first choice until the shopper edits the items
      this.idInput?.addEventListener('change', () => {
        this.tiers.forEach((t) => { if (t.dataset.dirty !== 'true') this.seed(t); });
        this.update();
      });
      const checked = qs('.bundle__input:checked', this);
      this.select(checked ? checked.closest('[data-bundle-tier]') : this.tiers[0]);
    }
    count(t) { return Number(t.dataset.paid) + Number(t.dataset.free); }
    current() { const id = Number(this.idInput?.value); return this.variants.find((v) => v.id === id) || this.variants[0]; }
    selectedTier() { return this.tiers.find((t) => qs('.bundle__input', t)?.checked) || this.tiers[0]; }
    find(opts) { return this.variants.find((v) => v.options.every((o, i) => o === opts[i])); }
    itemOptions(item) {
      return qsa('[data-bundle-option]', item).sort((a, b) => a.dataset.bundleOption - b.dataset.bundleOption).map((s) => s.value);
    }
    select(tier) {
      if (!tier) return;
      this.tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
      const input = qs('.bundle__input', tier);
      if (input && !input.checked) input.checked = true;
      if (tier.dataset.dirty !== 'true') this.seed(tier);
      this.section.classList.toggle('has-bundle-multi', this.count(tier) > 1);
      this.update();
    }
    // Default choices: the selected variant first, then the next available colours in the same size
    seed(tier) {
      const items = qsa('[data-bundle-item]', tier);
      const base = this.current();
      if (!items.length || !base) return;
      const ci = this.colorIndex;
      const colors = ci >= 0 ? [...new Set(this.variants.map((v) => v.options[ci]))] : [];
      const start = Math.max(0, colors.indexOf(base.options[ci]));
      items.forEach((item, n) => {
        let opts = base.options.slice();
        for (let k = 0; n > 0 && k < colors.length; k += 1) {
          const cand = opts.map((o, j) => (j === ci ? colors[(start + n + k) % colors.length] : o));
          if (this.find(cand)?.available) { opts = cand; break; }
        }
        qsa('[data-bundle-option]', item).forEach((sel) => { sel.value = opts[Number(sel.dataset.bundleOption)]; });
        this.refreshItem(item);
      });
    }
    refreshItem(item) {
      const opts = this.itemOptions(item);
      const v = this.find(opts);
      item.classList.toggle('is-unavailable', !v || !v.available);
      const img = qs('[data-bundle-thumb]', item);
      const src = v?.image || this.variants.find((x) => x.options[this.colorIndex] === opts[this.colorIndex] && x.image)?.image;
      if (img && src) { img.src = src; img.removeAttribute('srcset'); }
      const swatch = qs('[data-bundle-swatch]', item);
      if (swatch) swatch.setAttribute('style', this.swatches[opts[this.colorIndex]] || '');
    }
    lines(tier) {
      const items = qsa('[data-bundle-item]', tier);
      if (!items.length) return Array.from({ length: this.count(tier) }, () => this.current());
      return items.map((it) => this.find(this.itemOptions(it)));
    }
    update() {
      const cur = this.current();
      const chosen = this.selectedTier();
      this.tiers.forEach((t) => {
        const vs = t === chosen ? this.lines(t) : Array.from({ length: this.count(t) }, () => cur);
        if (!vs.length || vs.some((v) => !v)) return;
        // Shopify gives the cheapest items for free
        const price = vs.map((v) => v.price).sort((a, b) => a - b).slice(Number(t.dataset.free)).reduce((s, p) => s + p, 0);
        const compare = vs.reduce((s, v) => s + (this.dataset.basis === 'compare_at' && v.compare_at_price > v.price ? v.compare_at_price : v.price), 0);
        const priceEl = qs('[data-bundle-price]', t);
        if (priceEl) priceEl.textContent = formatMoney(price);
        const cmp = qs('[data-bundle-compare]', t);
        if (cmp) { cmp.textContent = formatMoney(compare); cmp.hidden = compare <= price; }
        const per = qs('[data-bundle-per]', t);
        if (per) per.textContent = formatMoney(Math.round(price / vs.length));
      });
      let msg = '';
      if (chosen && this.count(chosen) > 1) {
        const bad = this.lines(chosen).findIndex((v) => !v || !v.available);
        if (bad >= 0) msg = (theme.strings.bundleUnavailable || '').replace('__N__', bad + 1);
      }
      const err = qs('[data-bundle-error]', this);
      if (err) { err.textContent = msg; err.hidden = !msg; }
      const multi = !!chosen && this.count(chosen) > 1;
      const ok = multi ? !msg : !!(cur && cur.available);
      qsa('[data-add-to-cart]', this.section).forEach((b) => {
        b.disabled = !ok;
        const label = qs('[data-add-label]', b);
        if (label) label.textContent = ok || multi ? theme.strings.addToCart : theme.strings.soldOut;
      });
    }
    // Cart lines for the selected offer, or null for a single item (normal form submit)
    items() {
      const t = this.selectedTier();
      if (!t || this.count(t) < 2) return null;
      const vs = this.lines(t);
      if (vs.some((v) => !v || !v.available)) return [];
      const qty = new Map();
      vs.forEach((v) => qty.set(v.id, (qty.get(v.id) || 0) + 1));
      return [...qty].map(([id, quantity]) => ({ id, quantity }));
    }
  });

  /* ---------- Product: pack picker (one card per pack size, a colour and a size per item) ---------- */
  define('pack-picker', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]') || document;
      try { this.colors = JSON.parse(qs('script[data-pack-colors]', this).textContent); } catch (e) { this.colors = {}; }
      this.tiers = qsa('[data-pack-tier]', this);
      this.idInput = qs(`input[name="id"][data-product-id="${this.dataset.productId}"]`, this.section);
      this.form = this.idInput?.form;
      this.section.classList?.add('has-pack-picker');
      this.addEventListener('change', (e) => {
        if (e.target.matches('.bundle__input')) { this.select(e.target.closest('[data-pack-tier]'), true); return; }
        const item = e.target.closest('[data-pack-item]');
        if (!item) return;
        if (e.target.matches('[data-pack-size]')) {
          item.dataset.sized = 'true';
          // the first size picked also fills the rows that have no size yet
          qsa('[data-pack-item]', item.closest('[data-pack-tier]')).forEach((it) => {
            const s = qs('[data-pack-size]', it);
            if (it.dataset.sized !== 'true' && !s.value) s.value = e.target.value;
          });
        }
        this.refreshItem(item);
        this.sync();
      });
      // the variant picker (or a ?variant= URL) can change the pack too
      this.idInput?.addEventListener('change', () => {
        const t = this.tiers.find((x) => x.dataset.variantId === this.idInput.value);
        if (t && !t.classList.contains('is-selected')) this.select(t, false);
      });
      this.select(this.tiers.find((t) => t.classList.contains('is-selected')) || this.tiers[0], false);
    }
    selectedTier() { return this.tiers.find((t) => t.classList.contains('is-selected')); }
    select(tier, drive) {
      if (!tier) return;
      this.tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
      const input = qs('.bundle__input', tier);
      if (input && !input.checked) input.checked = true;
      if (drive && this.idInput && this.idInput.value !== tier.dataset.variantId) {
        // go through the hidden variant picker so price, URL, media and stock follow the pack
        const fs = qsa('variant-picker fieldset.option', this.section)[Number(this.dataset.packIndex)];
        const radio = fs && qsa('input[type="radio"]', fs).find((r) => r.value === tier.dataset.value);
        const select = fs && qs('select', fs);
        if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
        else if (select) { select.value = tier.dataset.value; select.dispatchEvent(new Event('change', { bubbles: true })); }
        else { this.idInput.value = tier.dataset.variantId; this.idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      this.sync();
    }
    refreshItem(item) {
      const info = this.colors[qs('[data-pack-color]', item)?.value] || {};
      const img = qs('[data-pack-thumb]', item);
      if (img) { img.hidden = !info.image; if (info.image) img.src = info.image; }
      const swatch = qs('[data-pack-swatch]', item);
      if (swatch) swatch.setAttribute('style', info.swatch || '');
    }
    missing() {
      const tier = this.selectedTier();
      return tier ? qsa('[data-pack-item]', tier).map((it, n) => (qs('[data-pack-size]', it).value ? -1 : n)).filter((n) => n >= 0) : [];
    }
    // One hidden "properties[Brief N]" input per item, inside the product form (also read by the express checkout buttons)
    sync() {
      const tier = this.selectedTier();
      if (!tier || !this.form) return;
      let box = qs('[data-pack-properties]', this.form);
      if (!box) { box = document.createElement('div'); box.hidden = true; box.dataset.packProperties = ''; this.form.prepend(box); }
      box.replaceChildren(...qsa('[data-pack-item]', tier).map((it, n) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${this.dataset.unit} ${n + 1}]`;
        input.value = [qs('[data-pack-color]', it).value, qs('[data-pack-size]', it).value].filter(Boolean).join(' / ');
        return input;
      }));
      const incomplete = this.missing().length > 0;
      this.section.classList?.toggle('pack-incomplete', incomplete);
      qsa('.shopify-payment-button', this.section).forEach((b) => { b.inert = incomplete; });
      if (!incomplete) this.showError('');
    }
    // Called by product-form before adding to the cart: every item needs a size
    validate() {
      const missing = this.missing();
      if (!missing.length) return true;
      this.showError((theme.strings.packChooseSize || '').replace('__U__', this.dataset.unit.toLowerCase()).replace('__N__', missing.map((n) => n + 1).join(', #')));
      qs('[data-pack-size]', qsa('[data-pack-item]', this.selectedTier())[missing[0]])?.focus();
      return false;
    }
    showError(msg) {
      const err = qs('[data-pack-error]', this);
      if (err) { err.textContent = msg; err.hidden = !msg; }
    }
  });

  define('cart-items', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', debounce((e) => {
        const input = e.target.closest('[data-line-qty]');
        if (input) this.update(input.dataset.line, input.value);
      }, 350));
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-line-remove]');
        if (rm) { e.preventDefault(); this.update(rm.dataset.line, 0); }
      });
    }
    async update(line, quantity) {
      this.classList.add('is-loading-grid');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: cartSectionIds(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (data.sections) updateCartSections(data.sections);
        else if (data.description) window.alert(data.description);
      } catch (err) {
        window.location.reload();
      } finally {
        this.classList.remove('is-loading-grid');
      }
    }
  });

  define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-step]');
        if (!b || !this.input) return;
        e.preventDefault();
        const min = Number(this.input.min || 0);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const v = Math.min(max, Math.max(min, Number(this.input.value || 0) + Number(b.dataset.step)));
        if (String(v) !== this.input.value) {
          this.input.value = v;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  });

  /* ---------- Product: variant picker ---------- */
  define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]') || document;
      try { this.variants = JSON.parse(qs('script[data-variants]', this).textContent); } catch (e) { this.variants = []; }
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    selected() {
      return qsa('fieldset.option', this).map((fs) => {
        const checked = qs('input:checked', fs);
        if (checked) return checked.value;
        const sel = qs('select', fs);
        return sel ? sel.value : null;
      });
    }
    updateAvailability() {
      const sel = this.selected();
      qsa('fieldset.option', this).forEach((fs, i) => {
        qsa('input', fs).forEach((input) => {
          const ok = this.variants.some((v) => v.available && v.options[i] === input.value && v.options.every((o, j) => j === i || o === sel[j]));
          input.closest('.option-btn')?.classList.toggle('is-unavailable', !ok);
        });
        qsa('option', fs).forEach((opt) => {
          const ok = this.variants.some((v) => v.available && v.options[i] === opt.value && v.options.every((o, j) => j === i || o === sel[j]));
          opt.textContent = opt.value + (ok ? '' : ` - ${theme.strings.unavailable}`);
        });
      });
    }
    onChange() {
      const opts = this.selected();
      qsa('[data-option-selected]', this).forEach((el, i) => { el.textContent = opts[i]; });
      this.updateAvailability();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      const buttons = qsa('[data-add-to-cart]', this.section);
      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; const l = qs('[data-add-label]', b); if (l) l.textContent = theme.strings.unavailable; });
        return;
      }
      qsa(`input[name="id"][data-product-id="${this.dataset.productId}"]`).forEach((input) => { input.value = variant.id; input.dispatchEvent(new Event('change', { bubbles: true })); });
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media) document.dispatchEvent(new CustomEvent('variant:media', { detail: { section: this.dataset.section, mediaId: variant.featured_media.id } }));
      this.render(variant.id);
    }
    async render(id) {
      this.pending = id;
      try {
        const res = await fetch(`${this.dataset.url}?variant=${id}&section_id=${this.dataset.section}`);
        const doc = parseHTML(await res.text());
        if (this.pending !== id) return;
        qsa('[data-variant-update]', this.section).forEach((el) => {
          const fresh = qs(`[data-variant-update="${el.dataset.variantUpdate}"]`, doc);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
      } catch (e) { /* keep current markup */ }
    }
  });

  /* ---------- Product: media gallery + lightbox ---------- */
  define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.main = qs('.gallery__main', this);
      this.slides = qsa('.gallery__slide', this);
      this.thumbs = qsa('.gallery__thumb', this);
      this.dots = qsa('.gallery__dot', this);
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));
      if (this.main && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting) this.setActive(this.slides.indexOf(en.target)); });
        }, { root: this.main, threshold: 0.6 });
        this.slides.forEach((s) => io.observe(s));
      }
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (z) this.openLightbox(Number(z.dataset.zoom), z);
      });
      this.onVariant = (e) => {
        if (e.detail.section !== this.dataset.section) return;
        const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(e.detail.mediaId));
        if (i > -1) this.goTo(i);
      };
      document.addEventListener('variant:media', this.onVariant);
    }
    disconnectedCallback() { document.removeEventListener('variant:media', this.onVariant); }
    goTo(i) {
      const s = this.slides[i];
      if (!s || !this.main) return;
      if (this.main.scrollWidth > this.main.clientWidth + 2) this.main.scrollTo({ left: s.offsetLeft - this.main.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      else s.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
      this.setActive(i);
    }
    setActive(i) {
      if (i < 0) return;
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      const counter = qs('[data-gallery-current]', this);
      if (counter) counter.textContent = String(i + 1);
      this.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    openLightbox(i, opener) {
      const id = this.dataset.lightbox;
      if (!openDialog(id, opener)) return;
      const target = qsa('[data-lightbox-index]', document.getElementById(id))[i];
      requestAnimationFrame(() => target?.scrollIntoView({ block: 'start' }));
    }
  });

  /* ---------- Sticky add to cart ---------- */
  define('sticky-atc', class extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([en]) => {
        const past = !en.isIntersecting && en.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', past);
        this.setAttribute('aria-hidden', String(!past));
        qsa('button, a', this).forEach((el) => { el.tabIndex = past ? 0 : -1; });
      }).observe(target);
    }
  });

  /* ---------- Sliders & tabs ---------- */
  define('slider-component', class extends HTMLElement {
    connectedCallback() {
      this.slider = qs('.slider', this);
      if (!this.slider) return;
      const id = this.slider.id;
      this.prev = qs('[data-slider-prev]', this) || (id && qs(`[data-slider-prev][aria-controls="${id}"]`));
      this.next = qs('[data-slider-next]', this) || (id && qs(`[data-slider-next][aria-controls="${id}"]`));
      this.prev?.addEventListener('click', () => this.move(-1));
      this.next?.addEventListener('click', () => this.move(1));
      this.slider.addEventListener('scroll', debounce(() => this.update(), 50), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.slider);
    }
    move(dir) {
      const item = this.slider.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : this.slider.clientWidth;
      this.slider.scrollBy({ left: dir * step, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.slider.scrollWidth - this.slider.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.slider.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.slider.scrollLeft >= max;
      const nav = this.prev?.closest('.slider-nav');
      if (nav) nav.hidden = max <= 0;
    }
  });

  define('tabs-component', class extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa('[role="tab"]', this);
      this.panels = qsa('[role="tabpanel"]', this);
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(n);
          this.tabs[n].focus();
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, n) => { t.setAttribute('aria-selected', String(n === i)); t.tabIndex = n === i ? 0 : -1; });
      this.panels.forEach((p, n) => { p.hidden = n !== i; });
    }
  });

  /* ---------- Predictive search ---------- */
  define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !this.results || !theme.routes.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.closest('dialog')?.addEventListener('dialog:open', () => setTimeout(() => this.input.focus(), 50));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      const params = new URLSearchParams({ q, section_id: 'predictive-search' });
      params.set('resources[type]', 'product,collection,page,query');
      params.set('resources[limit]', '6');
      params.set('resources[options][unavailable_products]', 'last');
      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`);
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        this.results.innerHTML = qs('#PredictiveResults', doc)?.innerHTML || '';
      } catch (e) { this.results.innerHTML = ''; }
    }
  });

  /* ---------- Collection filters & sorting ---------- */
  define('facet-filters', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', debounce((e) => { if (e.target.form && e.target.form.id === 'FacetsForm') this.apply(); }, 450));
      this.addEventListener('submit', (e) => { if (e.target.id === 'FacetsForm') { e.preventDefault(); this.apply(); } });
      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-facet-link]');
        if (a) { e.preventDefault(); this.load(a.href); }
      });
      window.addEventListener('popstate', () => this.load(window.location.href, false));
    }
    apply() {
      const form = document.getElementById('FacetsForm');
      if (!form) return;
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
      this.load(`${window.location.pathname}?${params.toString()}`);
    }
    async load(url, push = true) {
      const grid = qs('#ProductGridContainer');
      grid?.classList.add('is-loading-grid');
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.dataset.section);
      const activeId = document.activeElement?.id;
      try {
        const doc = parseHTML(await (await fetch(u.toString())).text());
        ['#ProductGridContainer', '#FacetsBody', '#ToolbarCount', '#ActiveFilters'].forEach((sel) => {
          const fresh = qs(sel, doc); const cur = qs(sel);
          if (fresh && cur) cur.replaceWith(fresh);
        });
        if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
        if (push) { u.searchParams.delete('section_id'); window.history.pushState({}, '', u.toString()); }
      } catch (e) {
        window.location.href = url;
      } finally {
        qs('#ProductGridContainer')?.classList.remove('is-loading-grid');
      }
    }
  });

  /* ---------- Product recommendations (related / complementary) ---------- */
  define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const io = new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
    async load() {
      try {
        const doc = parseHTML(await (await fetch(this.dataset.url)).text());
        const fresh = qs('product-recommendations', doc);
        if (fresh && fresh.querySelector('.card, .cart-upsell__item')) this.innerHTML = fresh.innerHTML;
        else this.closest('.shopify-section')?.setAttribute('hidden', '');
      } catch (e) { /* silent */ }
    }
  });

  /* ---------- Size finder ---------- */
  define('size-finder', class extends HTMLElement {
    connectedCallback() {
      try { this.rows = JSON.parse(qs('script[type="application/json"]', this).textContent); } catch (e) { this.rows = []; }
      this.unit = this.dataset.unit || 'cm';
      this.input = qs('input[type="number"]', this);
      this.result = qs('[data-result]', this);
      qsa('[data-unit]', this).forEach((b) => b.addEventListener('click', () => {
        this.unit = b.dataset.unit;
        qsa('[data-unit]', this).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        if (this.input) this.input.placeholder = this.unit === 'in' ? this.dataset.placeholderIn : this.dataset.placeholderCm;
        this.calc();
      }));
      this.input?.addEventListener('input', () => this.calc());
    }
    calc() {
      const v = parseFloat(this.input.value);
      qsa('tr[data-size-row]', this).forEach((r) => r.classList.remove('is-match'));
      if (!v || !this.rows.length) { this.result.hidden = true; return; }
      const cm = this.unit === 'in' ? v * 2.54 : v;
      let i = this.rows.findIndex((r) => cm >= r.min && cm <= r.max);
      let note = '';
      if (i === -1) {
        i = this.rows.findIndex((r) => cm < r.min);
        if (i === -1) { i = this.rows.length - 1; note = this.dataset.aboveText || ''; }
        else if (i > 0) note = this.dataset.betweenText || '';
      }
      const row = this.rows[i];
      this.result.innerHTML = `${this.dataset.resultText.replace('{size}', `<strong>${row.label}</strong>`)}${note ? `<br><small>${note}</small>` : ''}`;
      this.result.hidden = false;
      qsa('tr[data-size-row]', this)[i]?.classList.add('is-match');
    }
  });

  /* ---------- Reveal on scroll ---------- */
  if ('IntersectionObserver' in window && !reducedMotion && !window.Shopify?.designMode) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    const watch = (root = document) => qsa('.reveal:not(.is-visible)', root).forEach((el) => io.observe(el));
    watch();
    document.addEventListener('shopify:section:load', (e) => watch(e.target));
  } else {
    qsa('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Theme editor helpers ---------- */
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('#CartDrawer')) openDialog('CartDrawer');
  });
})();
